import { useEffect, useMemo, useState } from "react";
import { conversionsApi } from "../api/endpoints";
import {
  CURRENCY_SYMBOL,
  DASHBOARD_CURRENCY_ORDER,
} from "../utils/currencyMeta";

export const WALLET_DISPLAY_CURRENCIES = [...DASHBOARD_CURRENCY_ORDER, "GBP"];

export const useWalletEquivalent = (accounts, initialCurrency = "AUD") => {
  const [displayCurrency, setDisplayCurrency] = useState(initialCurrency);
  const [rates, setRates] = useState({});
  const [converting, setConverting] = useState(false);

  const openAccounts = useMemo(
    () => accounts.filter((account) => account.status !== "CLOSED"),
    [accounts]
  );

  const neededPairs = useMemo(() => {
    const pairs = new Set();
    openAccounts.forEach((account) => {
      const available = Number(account.availableBalance) || 0;
      if (available > 0 && account.currency !== displayCurrency) {
        pairs.add(`${account.currency}->${displayCurrency}`);
      }
    });
    return [...pairs];
  }, [displayCurrency, openAccounts]);

  useEffect(() => {
    const missingPairs = neededPairs.filter((pair) => !(pair in rates));
    if (!missingPairs.length) return;

    setConverting(true);
    Promise.all(
      missingPairs.map(async (pair) => {
        const [from, to] = pair.split("->");
        try {
          const rate = await conversionsApi.rate(from, to);
          return [pair, Number(rate?.effectiveRate ?? rate?.midRate ?? 0)];
        } catch {
          return [pair, 0];
        }
      })
    )
      .then((entries) => {
        setRates((previousRates) =>
          Object.fromEntries([...Object.entries(previousRates), ...entries])
        );
        setConverting(false);
      })
      .catch(() => setConverting(false));
  }, [neededPairs, rates]);

  /**
   * The converted total, and the wallets that could not be converted.
   *
   * A missing rate used to make a wallet contribute nothing, silently. Select
   * a display currency with no published rate and someone holding 100,000 AUD
   * was shown "Rs0.00", indistinguishable from an empty account. Money must
   * never appear to vanish because a rate lookup failed, so the two cases are
   * now reported separately and the caller decides what to show.
   */
  const { total, unconvertible } = useMemo(() => {
    const missing = [];
    const sum = openAccounts.reduce((running, account) => {
      const available = Number(account.availableBalance) || 0;
      if (available === 0) return running;
      if (account.currency === displayCurrency) return running + available;

      const rate = rates[`${account.currency}->${displayCurrency}`];
      if (rate && rate > 0) return running + available * rate;

      // Held, but not expressible in the chosen currency.
      if (!missing.includes(account.currency)) missing.push(account.currency);
      return running;
    }, 0);
    return { total: sum, unconvertible: missing };
  }, [displayCurrency, openAccounts, rates]);

  /** True when nothing at all could be converted, so the total is meaningless. */
  const totalUnavailable = unconvertible.length > 0 && total === 0;

  const formattedTotal = totalUnavailable
    ? "-"
    : (CURRENCY_SYMBOL[displayCurrency] || "") +
      total.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

  const displayCurrencyOptions = useMemo(
    () => WALLET_DISPLAY_CURRENCIES.map((currency) => ({ name: currency, value: currency })),
    []
  );

  return {
    converting,
    displayCurrency,
    displayCurrencyOptions,
    formattedTotal,
    openAccounts,
    setDisplayCurrency,
    total,
    unconvertible,
    totalUnavailable,
  };
};
